import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@lightspeed/flame/Card";
import { useSelector } from "react-redux";

import quickbooksIcon from "../home/assets/icons/quickbooks.png";
import xeroIcon from "../home/assets/icons/xero.png";
import sageIcon from "../home/assets/icons/sage.png";
import d365Icon from "../home/assets/icons/d365Icon.png";
import gpIcon from "../../assets/GP.jpg";
import { startOAuth } from "../../utils/oauth";
import { testAcombaSdk } from "../../utils/acombaSdk";

const acombaGoIcon = "/assets/icons/acomba_go.svg";
const acombaSdkIcon = "/assets/icons/acombaSDK.png";

const PROVIDERS = ["quickbooks", "xero", "sage", "D365BC", "GP", "ACOMBA_GO", "ACOMBA_SDK"];
const PENDING_OAUTH_PROVIDER_KEY = "pendingAccountingOAuthProvider";
const REPORT_API_URL = "https://reportapi.beesql.com/api/admin/retailr/report";
const ACOMBA_SDK_GET_XOPTION = "ACOMBA_SDK_SETTINGS_GET";
const ACOMBA_SDK_SAVE_XOPTION = "ACOMBA_SDK_SETTINGS_SAVE";
const ACOMBA_SDK_TESTS = [
  { label: "Service Test", endpoint: "test" },
  { label: "Acomba Test", endpoint: "service" },
  { label: "API Test", endpoint: "accounts" },
];

/** Normalize backend value to a supported accounting provider key. */
function normalizeIntegrationKey(val) {
  if (!val) return "NONE";
  const v = String(val).trim().toLowerCase();
  if (v.startsWith("qb") || v.includes("quickbooks")) return "quickbooks";
  if (v.includes("xero")) return "xero";
  if (v.includes("sage")) return "sage";
  if (v.includes("d365bc") || v.includes("d365") || v.includes("business central")) return "D365BC";
  if (v === "gp" || v.includes("dynamics gp") || v.includes("great plains")) return "GP";
  if (v === "acombago" || v === "acomba_go" || v.includes("acomba go")) return "ACOMBA_GO";
  if (v === "acombasdk" || v === "acomba_sdk" || v.includes("acomba sdk")) return "ACOMBA_SDK";
  return "NONE";
}

function getIntegrationValue(data) {
  const source = Array.isArray(data) ? data[0] : data;
  return (
    source?.INTEGRATION_NAME ??
    source?.integration_name ??
    source?.activeIntegration ??
    source?.provider ??
    source?.integration ??
    "NONE"
  );
}
/** Normalize to boolean for PayPal active flag */
function toBool(v) {
  return v === true || v === "true" || v === 1 || v === "1";
}

const Dash = () => {
  const userState = useSelector((state) => state.userState);
  const userId = userState?.user?.user_id;

  const [quantity, setQuantity] = useState(1);

  // Aktivna integracija – "NONE" znači da nijedna nije spojena
  const [activeIntegration, setActiveIntegration] = useState("NONE");

  // PayPal aktivan kao boolean (ne "NONE")
  const [activeIntegrationPaypal, setActiveIntegrationPaypal] = useState(false);

  const [isFetchingIntegration, setIsFetchingIntegration] = useState(false);
  const [isAcombaModalOpen, setIsAcombaModalOpen] = useState(false);
  const [selectedAcombaProvider, setSelectedAcombaProvider] = useState(null);
  const [acombaSdkSettings, setAcombaSdkSettings] = useState({ ip: "", port: "" });
  const [isLoadingAcombaSdk, setIsLoadingAcombaSdk] = useState(false);
  const [isSavingAcombaSdk, setIsSavingAcombaSdk] = useState(false);
  const [acombaSdkError, setAcombaSdkError] = useState("");
  const [runningAcombaTest, setRunningAcombaTest] = useState(null);
  const [acombaTestResults, setAcombaTestResults] = useState({});
  const [acombaCredentials, setAcombaCredentials] = useState({
    key: "",
    tenant: "",
    developerKey: "",
  });

  // PayPal refs za kontrolu renderiranja
  const paypalContainerRef = useRef(null);
  const paypalButtonsRef = useRef(null);
  const pendingOAuthProviderRef = useRef(null);

  const fetchActiveIntegration = useCallback(async (signal) => {
    try {
      setIsFetchingIntegration(true);
      const url = `https://reportapi.beesql.com/api/admin/retailr/report`;

      const res = await fetch(url, {
        method: "POST",
        signal,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId, // ako treba slati user_id
          xoption: "SP_LightspeedCheckActiveIntegration",
        }),
      });

      if (!res.ok) {
        throw new Error(`Active integration fetch failed: ${res.status}`);
      }

      const data = await res.json();
      console.log("SP_LightspeedCheckActiveIntegration response:", data);

      // Probaj više polja (ovisno kako backend šalje)
      const raw = getIntegrationValue(data);
      const normalized = normalizeIntegrationKey(raw);
      console.log("Active integration normalized:", { raw, normalized });
      setActiveIntegration(PROVIDERS.includes(normalized) ? normalized : "NONE");

      // PayPal kao boolean (podržava true/"true"/1/"1")
      setActiveIntegrationPaypal(toBool(data?.activeIntegrationPaypal));

      return normalized;
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Failed to fetch active integration:", err);
        setActiveIntegration("NONE");
      }
      return "NONE";
    } finally {
      setIsFetchingIntegration(false);
    }
  }, [userId]);

  // ===== 1) U startu: povuci aktivnu integraciju iz baze =====
  useEffect(() => {
    const ac = new AbortController();

    async function fetchActiveIntegration() {
      try {
        setIsFetchingIntegration(true);
        const url = `https://reportapi.beesql.com/api/admin/retailr/report`;

        const res = await fetch(url, {
          method: "POST",
          signal: ac.signal,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId, // ako treba slati user_id
            xoption: "SP_LightspeedCheckActiveIntegration",
          }),
        });

        if (!res.ok) {
          throw new Error(`Active integration fetch failed: ${res.status}`);
        }

        const data = await res.json();
        console.log("SP_LightspeedCheckActiveIntegration response:", data);

        // Probaj više polja (ovisno kako backend šalje)
        const raw = getIntegrationValue(data);
        const normalized = normalizeIntegrationKey(raw);
        console.log("Active integration normalized:", { raw, normalized });
        setActiveIntegration(PROVIDERS.includes(normalized) ? normalized : "NONE");

        // PayPal kao boolean (podržava true/"true"/1/"1")
        setActiveIntegrationPaypal(toBool(data?.activeIntegrationPaypal));
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Failed to fetch active integration:", err);
          setActiveIntegration("NONE");
        }
      } finally {
        setIsFetchingIntegration(false);
      }
    }

    // Ako imamo userId, povuci; ako nema, svejedno pokušaj (backend može pročitati iz sesije)
    fetchActiveIntegration();

    return () => ac.abort();
  }, [userId]);

  // (Opcionalno) logaj promjene PayPal stanja
  useEffect(() => {
    // console.log("activeIntegrationPaypal -> ", activeIntegrationPaypal);
  }, [activeIntegrationPaypal]);

  // ===== 2) OAuth povratna poruka (QB primjer) =====
  useEffect(() => {
    function onMsg(e) {
      // TODO: radi sigurnosti ograniči na dopušteni origin, npr.:
      // if (e.origin !== "https://tvoja-domena.com") return;

      const data = e.data;
      if (!data || !String(data.type || "").startsWith("oauth:")) return;

      const pendingProvider = normalizeIntegrationKey(
        pendingOAuthProviderRef.current || window.localStorage.getItem(PENDING_OAUTH_PROVIDER_KEY)
      );
      const typeProvider = normalizeIntegrationKey(String(data.type || "").replace(/^oauth:/, ""));
      const messageProvider = normalizeIntegrationKey(data.provider);
      const provider = PROVIDERS.includes(pendingProvider)
        ? pendingProvider
        : PROVIDERS.includes(typeProvider)
        ? typeProvider
        : messageProvider;

      console.log("OAuth message received:", {
        type: data.type,
        providerFromMessage: data.provider,
        pendingProvider,
        typeProvider,
        messageProvider,
        selectedProvider: provider,
      });

      if (data.status === "success") {
        setActiveIntegration(PROVIDERS.includes(provider) ? provider : "NONE"); // vizualni feedback
        fetchActiveIntegration().then((normalized) => {
          console.log("Active integration refreshed after OAuth:", normalized);
        });
        // ovdje pošalji data.code + data.realmId svom API-ju za token exchange
      } else {
        console.error("OAuth error:", provider, data.error, data.error_description);
        alert(`${provider || "Accounting"} OAuth failed: ${data.error || "Unknown error"}`);
      }

      pendingOAuthProviderRef.current = null;
      window.localStorage.removeItem(PENDING_OAUTH_PROVIDER_KEY);
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [fetchActiveIntegration]);

  // ===== 3) Forsiraj bijelu pozadinu =====
  useEffect(() => {
    document.body.style.backgroundColor = "#ffffff";
    document.documentElement.style.backgroundColor = "#ffffff";
  }, []);

  // ===== 4) PayPal SDK + render gumba (sigurno i nakon povratka na stranicu) =====
  useEffect(() => {
    // Funkcija koja sigurno (re)rendere gumbe
    const renderButtons = () => {
      if (!window.paypal || !paypalContainerRef.current) return;

      // Očisti prethodni render (bitno kod povratka na stranicu)
      if (paypalButtonsRef.current?.close) {
        try {
          paypalButtonsRef.current.close();
        } catch (e) {
          // ignore
        }
      }
      paypalButtonsRef.current = null;
      paypalContainerRef.current.innerHTML = "";

      paypalButtonsRef.current = window.paypal.Buttons({
        style: {
          shape: "pill",
          color: "blue",
          layout: "vertical",
          label: "subscribe",
        },
        createSubscription: (data, actions) => {
          return actions.subscription.create({
            plan_id: "P-04W16877RL992274GNCKOHEQ",
            quantity: Number(quantity),
          });
        },
        onApprove: (data) => {
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Comapny-Id": "1",
              "X-Location-Id": "1",
              "X-User-Id": "1",
            },
            body: JSON.stringify({
              paypal_subscription_id: data.subscriptionID,
              paypal_order_id: data.orderID,
              paypal_payer_id: data.payerID,
              operation_id: "LIGHTSPEED_PAYPAL_SUBSCRIPTION",
              user_id: userId,
            }),
          }).catch((err) => console.error("PayPal API error:", err));
        },
      });

      // render u container (ref, ne string id)
      paypalButtonsRef.current.render(paypalContainerRef.current);
    };

    const ensureSdkAndRender = () => {
      // ako je SDK već učitan – samo render
      if (window.paypal) {
        renderButtons();
        return;
      }

      // ako nije – injektaj pa renderaj u onload
      let script = document.getElementById("paypal-sdk");
      if (!script) {
        script = document.createElement("script");
        script.id = "paypal-sdk";
        script.src =
          "https://www.paypal.com/sdk/js?client-id=AZ0jImltAFOy-mtGPCpxVbY9iV3BRLtYYsv0_LKUWfXz62HPOr1TggK0xa0_cOL_NgMBfC0Nk1EZCYrt&vault=true&intent=subscription";
        script.addEventListener("load", renderButtons, { once: true });
        document.body.appendChild(script);
      } else {
        // skripta postoji, možda još nije loaded – dodaj onload handler ako treba
        script.addEventListener("load", renderButtons, { once: true });
        // ili ako je već loadana:
        if (window.paypal) renderButtons();
      }
    };

    ensureSdkAndRender();

    // cleanup pri unmountu ili izmjeni deps-a
    return () => {
      if (paypalButtonsRef.current?.close) {
        try {
          paypalButtonsRef.current.close();
        } catch (e) {
          // ignore
        }
      }
    };
  }, [quantity, userId]); // promjena quantity treba re-render gumba

  // helper: set active by click or keyboard
  const activate = (key) => setActiveIntegration(key);
  const startProviderOAuth = (key) => {
    if (key === "ACOMBA_GO" || key === "ACOMBA_SDK") {
      setAcombaTestResults({});
      setSelectedAcombaProvider(key);
      setIsAcombaModalOpen(true);
      return;
    }

    pendingOAuthProviderRef.current = key;
    window.localStorage.setItem(PENDING_OAUTH_PROVIDER_KEY, key);
    activate(key);
    startOAuth(key, userId);
  };
  const onKeyActivate = (e, key) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      startProviderOAuth(key);
    }
  };

  useEffect(() => {
    if (!isAcombaModalOpen || selectedAcombaProvider !== "ACOMBA_SDK") return undefined;

    const controller = new AbortController();

    async function fetchAcombaSdkSettings() {
      try {
        setIsLoadingAcombaSdk(true);
        setAcombaSdkError("");
        const response = await fetch(REPORT_API_URL, {
          method: "POST",
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token_user_id: userId, xoption: ACOMBA_SDK_GET_XOPTION }),
        });

        if (!response.ok) throw new Error(`Acomba SDK settings fetch failed: ${response.status}`);

        const data = await response.json();
        const rawSettings = data?.settings ?? data?.acombaSdkSettings ?? data ?? {};
        const settings = Array.isArray(rawSettings) ? rawSettings[0] ?? {} : rawSettings;
        setAcombaSdkSettings({
          ip: String(settings.ACOMBA_SDK_IP ?? settings.ip ?? settings.IP ?? settings.host ?? ""),
          port: String(settings.ACOMBA_SDK_PORT ?? settings.port ?? settings.PORT ?? ""),
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Failed to fetch Acomba SDK settings:", error);
          setAcombaSdkError("Could not load Acomba SDK settings.");
        }
      } finally {
        if (!controller.signal.aborted) setIsLoadingAcombaSdk(false);
      }
    }

    fetchAcombaSdkSettings();
    return () => controller.abort();
  }, [isAcombaModalOpen, selectedAcombaProvider, userId]);

  const runAcombaSdkTest = async ({ label, endpoint }) => {
    if (runningAcombaTest) return;
    setRunningAcombaTest(endpoint);
    setAcombaTestResults((current) => ({ ...current, [endpoint]: null }));
    try {
      const responseText = await testAcombaSdk({
        ip: acombaSdkSettings.ip,
        port: acombaSdkSettings.port,
        endpoint,
      });
      setAcombaTestResults((current) => ({
        ...current,
        [endpoint]: { message: responseText || `${label} completed.`, error: false },
      }));
    } catch (error) {
      setAcombaTestResults((current) => ({
        ...current,
        [endpoint]: { message: error.message || `${label} failed.`, error: true },
      }));
    } finally {
      setRunningAcombaTest(null);
    }
  };

  const saveAcombaSdkSettings = async () => {
    try {
      setIsSavingAcombaSdk(true);
      setAcombaSdkError("");
      const response = await fetch(REPORT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token_user_id: userId,
          ip: acombaSdkSettings.ip.trim(),
          port: Number(acombaSdkSettings.port),
          xoption: ACOMBA_SDK_SAVE_XOPTION,
        }),
      });

      if (!response.ok) throw new Error(`Acomba SDK settings save failed: ${response.status}`);

      setActiveIntegration("ACOMBA_SDK");
      setIsAcombaModalOpen(false);
    } catch (error) {
      console.error("Failed to save Acomba SDK settings:", error);
      setAcombaSdkError("Could not save Acomba SDK settings.");
    } finally {
      setIsSavingAcombaSdk(false);
    }
  };
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
        backgroundColor: "#ffffff",
        gap: "30px",
      }}
    >
      {/* Cards wrapper */}
      <div
        style={{
          display: "flex",
          gap: "30px",
          alignItems: "flex-start",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <div> </div>
        <br />
        {/* Accounting systems */}
        <Card
          className="p-10"
          style={{
            width: "900px",
            textAlign: "center",
            background: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div> </div>
          <br />
          <h3 className="mb-8 text-lg font-medium">
            {isFetchingIntegration ? "Loading integration..." : "Select an accounting system"}
            <br />
            <br />
          </h3>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "stretch",
              gap: "20px",
            }}
          >
            {[
              { key: "quickbooks", label: "QuickBooks", icon: quickbooksIcon },
              { key: "xero", label: "Xero", icon: xeroIcon },
              { key: "sage", label: "Sage", icon: sageIcon },
              { key: "D365BC", label: "D365 Business Central", icon: d365Icon },
              { key: "GP", label: "Microsoft Dynamics GP", icon: gpIcon },
              { key: "ACOMBA_GO", label: "Acomba GO", icon: acombaGoIcon },
              { key: "ACOMBA_SDK", label: "Acomba SDK", icon: acombaSdkIcon },
            ].map((p) => {
              const isActive = activeIntegration === p.key;
              const hasAnyActive = activeIntegration !== "NONE";
              const disabled = hasAnyActive && !isActive;

              return (
                <div
                  key={p.key}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isActive}
                  aria-disabled={disabled}
                  onClick={() => {
                    if (disabled) return;
                    startProviderOAuth(p.key);
                  }}
                  onKeyDown={(e) => {
                    if (disabled) return;
                    onKeyActivate(e, p.key);
                  }}
                  title={p.label}
                  style={{
                    flex: "1 1 0",
                    position: "relative",
                    padding: "10px",
                    borderRadius: 8,
                    background: "#fff",
                    border: isActive ? "2px solid #16a34a" : "2px solid transparent",
                    boxShadow: isActive
                      ? "0 4px 12px rgba(0,0,0,0.12)"
                      : "0 2px 6px rgba(0,0,0,0.06)",
                    cursor: disabled ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    outline: "none",
                    opacity: disabled ? 0.5 : 1,
                    pointerEvents: disabled ? "none" : "auto",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive && !disabled) e.currentTarget.style.borderColor = "#0070ba";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = isActive ? "#16a34a" : "transparent";
                  }}
                  onFocus={(e) => {
                    if (!isActive && !disabled) e.currentTarget.style.borderColor = "#0070ba";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = isActive ? "#16a34a" : "transparent";
                  }}
                >
                  {isActive && (
                    <span
                      style={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        background: "#16a34a",
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 600,
                        borderRadius: 999,
                        padding: "4px 10px",
                      }}
                    >
                      Connected
                    </span>
                  )}
                  <img
                    src={p.icon}
                    alt={p.label}
                    style={{
                      width: "100%",
                      height: "168px",
                      objectFit: "contain",
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              );
            })}
          </div>
        </Card>

        {isAcombaModalOpen && (
          <div
            role="presentation"
            onClick={() => setIsAcombaModalOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              background: "rgba(15, 23, 42, 0.48)",
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="acomba-modal-title"
              onClick={(event) => event.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: "480px",
                maxHeight: "calc(100vh - 40px)",
                overflowY: "auto",
                padding: "28px",
                borderRadius: "12px",
                background: "#fff",
                boxShadow: "0 20px 50px rgba(15, 23, 42, 0.24)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "24px",
                }}
              >
                <h2 id="acomba-modal-title" style={{ margin: 0, fontSize: "22px" }}>
                  {selectedAcombaProvider === "ACOMBA_SDK" ? "Acomba SDK" : "Acomba GO"}
                </h2>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setIsAcombaModalOpen(false)}
                  style={{
                    border: 0,
                    background: "transparent",
                    fontSize: "28px",
                    lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  &times;
                </button>
              </div>

              {(selectedAcombaProvider === "ACOMBA_SDK"
                ? [
                    { name: "ip", label: "IP address", type: "text" },
                    { name: "port", label: "Port", type: "number" },
                  ]
                : [
                    { name: "key", label: "Acomba Key", type: "password" },
                    { name: "tenant", label: "Acomba Tenant", type: "text" },
                    { name: "developerKey", label: "Acomba Developer Key", type: "password" },
                  ]
              ).map((field) => {
                const values =
                  selectedAcombaProvider === "ACOMBA_SDK" ? acombaSdkSettings : acombaCredentials;
                const setValues =
                  selectedAcombaProvider === "ACOMBA_SDK"
                    ? setAcombaSdkSettings
                    : setAcombaCredentials;

                return (
                  <label
                    key={field.name}
                    style={{ display: "block", marginBottom: "18px", textAlign: "left" }}
                  >
                    <span style={{ display: "block", marginBottom: "7px", fontWeight: 600 }}>
                      {field.label}
                    </span>
                    <input
                      type={field.type}
                      min={field.name === "port" ? "1" : undefined}
                      max={field.name === "port" ? "65535" : undefined}
                      value={values[field.name]}
                      onChange={(event) => {
                        const value = event.target.value;
                        setValues((current) => ({ ...current, [field.name]: value }));
                      }}
                      disabled={selectedAcombaProvider === "ACOMBA_SDK" && isLoadingAcombaSdk}
                      autoComplete="off"
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "11px 12px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "7px",
                        fontSize: "15px",
                      }}
                    />
                  </label>
                );
              })}

              {selectedAcombaProvider === "ACOMBA_SDK" && (
                <div
                  style={{
                    marginTop: "24px",
                    paddingTop: "20px",
                    borderTop: "1px solid #e2e8f0",
                    display: "grid",
                    gap: "14px",
                  }}
                >
                  {ACOMBA_SDK_TESTS.map((test) => {
                    const result = acombaTestResults[test.endpoint];
                    const disabled = isLoadingAcombaSdk || isSavingAcombaSdk ||
                      Boolean(runningAcombaTest) ||
                      !acombaSdkSettings.ip.trim() || !acombaSdkSettings.port ||
                      !Number.isInteger(Number(acombaSdkSettings.port)) ||
                      Number(acombaSdkSettings.port) < 1 || Number(acombaSdkSettings.port) > 65535;
                    return (
                      <div key={test.endpoint} style={{ padding: "14px", border: "1px solid #e2e8f0", borderRadius: "8px", background: "#f8fafc" }}>
                        <button
                          type="button"
                          onClick={() => runAcombaSdkTest(test)}
                          disabled={disabled}
                          aria-busy={runningAcombaTest === test.endpoint}
                          style={{
                            width: "100%",
                            padding: "10px 16px",
                            border: "1px solid #0070ba",
                            borderRadius: "7px",
                            background: "#fff",
                            color: "#0070ba",
                            fontWeight: 600,
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {runningAcombaTest === test.endpoint ? "Testing..." : test.label}
                        </button>
                        {result && (
                          <div
                            role={result.error ? "alert" : "status"}
                            style={{ marginTop: "10px", color: result.error ? "#b91c1c" : "#334155", whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: "140px", overflowY: "auto", textAlign: "left", fontSize: "13px" }}
                          >
                            {result.message}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedAcombaProvider === "ACOMBA_SDK" && acombaSdkError && (
                <div role="alert" style={{ color: "#b91c1c", marginTop: "4px", textAlign: "left" }}>
                  {acombaSdkError}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "26px" }}>
                <button
                  type="button"
                  onClick={
                    selectedAcombaProvider === "ACOMBA_SDK"
                      ? saveAcombaSdkSettings
                      : () => setIsAcombaModalOpen(false)
                  }
                  disabled={
                    selectedAcombaProvider === "ACOMBA_SDK" &&
                    (isLoadingAcombaSdk ||
                      isSavingAcombaSdk ||
                      Boolean(runningAcombaTest) ||
                      !acombaSdkSettings.ip.trim() ||
                      !acombaSdkSettings.port ||
                      Number(acombaSdkSettings.port) < 1 ||
                      Number(acombaSdkSettings.port) > 65535)
                  }
                  style={{
                    minWidth: "140px",
                    padding: "12px 28px",
                    border: "1px solid #15803d",
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #22c55e 0%, #15803d 100%)",
                    boxShadow: "0 6px 14px rgba(22, 163, 74, 0.25)",
                    color: "#fff",
                    fontSize: "15px",
                    fontWeight: 700,
                    letterSpacing: "0.01em",
                    cursor: "pointer",
                  }}
                >
                  {isSavingAcombaSdk ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PayPal card */}
        <Card
          className="p-10"
          style={{
            width: "400px",
            textAlign: "center",
            background: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e5e7eb",
            display: "none"
          }}
        >
          <h3
            style={{
              fontSize: "1.5rem",
              fontWeight: "600",
              marginBottom: "1.5rem",
              color: "#1a1a1a",
            }}
          >
            Subscribe via PayPal
          </h3>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "15px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <label
              htmlFor="locationQty"
              style={{
                fontWeight: "500",
                fontSize: "1rem",
                color: "#333",
              }}
            >
              Quantity (Number of Locations):
            </label>
            <input
              id="locationQty"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              style={{
                border: "2px solid #ddd",
                borderRadius: "8px",
                padding: "8px 12px",
                width: "80px",
                fontSize: "1rem",
                textAlign: "center",
                outline: "none",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#0070ba")}
              onBlur={(e) => (e.target.style.borderColor = "#ddd")}
            />
          </div>

          {/* umjesto id-a koristimo ref */}
          <div ref={paypalContainerRef} style={{ marginTop: "10px" }} />
        </Card>
      </div>
    </div>
  );
};

export default Dash;

